/**
 * Phase 18.16-B Batch 4 — Misc pages admin sub-section.
 *
 * Mirrors the Batch 2/3 editors exactly. Slug picker +
 * section tabs (page_banner / intro / cta_band) per page.
 *
 * Backend: /api/admin/misc-pages/{slug}/*
 *
 * Editor UX rules (PRD R4): all wrapper sub-components are defined at
 * top level → stable identity → no focus-loss on keystrokes. Controlled
 * inputs default to "" so React never sees undefined.
 */
import React, { useCallback, useEffect, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, Loader2, ShoppingCart, Receipt, ShieldCheck,
} from "lucide-react";

const SLUGS = [
  { value: "webshop",        label: "Webshop",          icon: ShoppingCart },
  { value: "offerte-share",  label: "Offerte share",    icon: Receipt },
  { value: "my5999cargo",    label: "MY5999",           icon: ShieldCheck },
];

const ICON_OPTIONS = [
  "ship", "plane", "package", "truck", "car", "map", "briefcase", "user",
  "check", "clock", "shield", "banknote", "support", "info", "receipt", "zap",
];

const SECTIONS = [
  { key: "page_banner", label: "Page banner" },
  { key: "intro",       label: "Intro" },
  { key: "cta_band",    label: "CTA-band" },
];

function sectionLabel(key) {
  return (SECTIONS.find((s) => s.key === key) || {}).label || key;
}

export default function MiscPagesSubSection() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("website:edit") || hasPermission("admin:all");

  const [slug, setSlug] = useState(SLUGS[0].value);
  const [activeSection, setActiveSection] = useState("page_banner");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState(null);

  const load = useCallback(async (newSlug) => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/misc-pages/${newSlug}`);
      setData(r.data || null);
    } catch (e) {
      toast.error(formatApiError(e, "Kon instellingen niet laden"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(slug); }, [load, slug]);

  const setField = (sec, k, v) => setData((d) => ({
    ...d,
    sections: { ...d.sections, [sec]: { ...(d.sections[sec] || {}), [k]: v } },
  }));
  const setListField = (sec, listKey, idx, k, v) => setData((d) => {
    const list = [...(d.sections[sec]?.[listKey] || [])];
    list[idx] = { ...(list[idx] || {}), [k]: v };
    return { ...d, sections: { ...d.sections, [sec]: { ...(d.sections[sec] || {}), [listKey]: list } } };
  });
  const addListItem = (sec, listKey, template) => setData((d) => {
    const list = [...(d.sections[sec]?.[listKey] || [])];
    list.push({ ...template });
    return { ...d, sections: { ...d.sections, [sec]: { ...(d.sections[sec] || {}), [listKey]: list } } };
  });
  const removeListItem = (sec, listKey, idx) => setData((d) => {
    const list = [...(d.sections[sec]?.[listKey] || [])];
    list.splice(idx, 1);
    return { ...d, sections: { ...d.sections, [sec]: { ...(d.sections[sec] || {}), [listKey]: list } } };
  });

  const saveSection = async (sec) => {
    if (!canEdit) return;
    setSavingSection(sec);
    try {
      const r = await adminApi.put(`/admin/misc-pages/${slug}/${sec}`, {
        payload: data.sections[sec] || {},
      });
      if (r.data) setData(r.data);
      toast.success(`${sectionLabel(sec)} opgeslagen`);
    } catch (e) {
      toast.error(formatApiError(e, "Opslaan mislukt"));
    } finally {
      setSavingSection(null);
    }
  };

  if (loading || !data) {
    return (
      <div className="py-10 text-center text-gray-400 text-sm">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
        Overige pagina laden...
      </div>
    );
  }

  const activeSec = data.sections[activeSection] || {};
  const isShare = slug === "offerte-share";

  return (
    <div data-testid="misc-pages-sub-section">
      {!canEdit && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
          U heeft alleen leesrechten — wijzigen vereist Owner/Admin of Content Manager.
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-6">
        {SLUGS.map((s) => {
          const Icon = s.icon;
          const active = slug === s.value;
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => { setSlug(s.value); setActiveSection("page_banner"); }}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${active ? "bg-[#082c50] text-white" : "bg-slate-100 text-[#082c50] hover:bg-slate-200"}`}
              data-testid={`misc-page-slug-${s.value}`}
            >
              <Icon className="w-4 h-4" /> {s.label}
            </button>
          );
        })}
      </div>

      {isShare && (
        <div className="mb-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-900 leading-relaxed" data-testid="misc-share-warning">
          <strong>Offerte share page — wat is publiek zichtbaar?</strong> Deze
          pagina is token-gated en bedoeld voor één specifieke klant per link.
          Op dit moment renderen we: <code>page_banner.eyebrow</code> (Cargo with
          a smile strip), <code>page_banner.title</code> + <code>highlight</code>{" "}
          (H1) en <code>page_banner.subtitle</code> (helper-tekst). De{" "}
          <em>Intro</em>-velden worden gebruikt voor de "Belangrijke informatie"
          sectie onderaan. De <em>CTA-band</em> wordt voor deze pagina{" "}
          <strong>niet</strong> publiek gerenderd — Bel/Mail-knoppen blijven
          hardcoded om de security van de share-link te bewaken.
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4 border-b border-slate-200 pb-3">
        {SECTIONS.map((sec) => (
          <button
            key={sec.key}
            type="button"
            onClick={() => setActiveSection(sec.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${activeSection === sec.key ? "bg-[#082c50] text-white" : "bg-slate-100 text-[#082c50] hover:bg-slate-200"}`}
            data-testid={`misc-page-section-${sec.key}`}
          >
            {sec.label}
          </button>
        ))}
      </div>

      <SectionForm
        sectionKey={activeSection}
        values={activeSec}
        slug={slug}
        canEdit={canEdit}
        saving={savingSection === activeSection}
        onSave={() => saveSection(activeSection)}
        onChange={(k, v) => setField(activeSection, k, v)}
        onListChange={(listKey, idx, k, v) => setListField(activeSection, listKey, idx, k, v)}
        onListAdd={(listKey, tpl) => addListItem(activeSection, listKey, tpl)}
        onListRemove={(listKey, idx) => removeListItem(activeSection, listKey, idx)}
      />

      <p className="mt-4 text-xs text-gray-500 leading-relaxed">
        Wijzigingen verschijnen direct op de publieke pagina. Webshop
        producten / Stripe checkout / cart, offerte share PDF download /
        token-security en MY5999 login-flow blijven volledig ongewijzigd.
      </p>
    </div>
  );
}

function SectionForm({
  sectionKey, values, slug, canEdit, saving, onSave,
  onChange, onListChange, onListAdd, onListRemove,
}) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4"
      data-testid={`misc-page-editor-${slug}-${sectionKey}`}
    >
      <div className="flex items-center justify-between">
        <h3 className="font-chivo font-bold text-lg text-[#082c50]">{sectionLabel(sectionKey)}</h3>
        <Button
          onClick={onSave}
          disabled={!canEdit || saving}
          className="rounded-xl bg-[#082c50] hover:bg-[#0a335c] text-white"
          data-testid={`misc-page-save-${sectionKey}`}
        >
          {saving
            ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Opslaan...</>
            : <><Save className="w-4 h-4 mr-1" />Opslaan</>}
        </Button>
      </div>

      {sectionKey === "page_banner" && (
        <>
          <TextRow label="Eyebrow" v={values.eyebrow} onChange={(v) => onChange("eyebrow", v)} testid={`${slug}-banner-eyebrow`} disabled={!canEdit} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextRow label="Hoofdtitel" v={values.title} onChange={(v) => onChange("title", v)} testid={`${slug}-banner-title`} disabled={!canEdit} />
            <TextRow label="Highlight (gradient)" v={values.highlight} onChange={(v) => onChange("highlight", v)} testid={`${slug}-banner-highlight`} disabled={!canEdit} />
          </div>
          <AreaRow label="Subtitle" v={values.subtitle} onChange={(v) => onChange("subtitle", v)} testid={`${slug}-banner-subtitle`} disabled={!canEdit} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextRow label="Primary CTA label" v={values.cta_primary_label} onChange={(v) => onChange("cta_primary_label", v)} disabled={!canEdit} />
            <TextRow label="Primary CTA URL" v={values.cta_primary_url} onChange={(v) => onChange("cta_primary_url", v)} disabled={!canEdit} />
            <TextRow label="Secondary CTA label" v={values.cta_secondary_label} onChange={(v) => onChange("cta_secondary_label", v)} disabled={!canEdit} />
            <TextRow label="Secondary CTA URL" v={values.cta_secondary_url} onChange={(v) => onChange("cta_secondary_url", v)} disabled={!canEdit} />
          </div>
          <ListEditor
            label="USP-badges (max 5)"
            items={values.badges || []}
            fields={[
              { key: "icon",  label: "Icon",  type: "select", options: ICON_OPTIONS },
              { key: "label", label: "Label", type: "text" },
            ]}
            onChange={(idx, f, v) => onListChange("badges", idx, f, v)}
            onAdd={() => onListAdd("badges", { icon: "shield", label: "" })}
            onRemove={(idx) => onListRemove("badges", idx)}
            canEdit={canEdit}
            max={5}
          />
        </>
      )}

      {sectionKey === "intro" && (
        <>
          <TextRow label="Eyebrow" v={values.eyebrow} onChange={(v) => onChange("eyebrow", v)} disabled={!canEdit} />
          <TextRow label="Sectietitel (H2)" v={values.title} onChange={(v) => onChange("title", v)} disabled={!canEdit} />
          <AreaRow label="Helper tekst / uitleg" v={values.body} onChange={(v) => onChange("body", v)} disabled={!canEdit} />
          {slug === "offerte-share" ? (
            <p className="text-xs text-blue-900 italic">
              Voor de offerte share page worden deze velden gebruikt als
              "Belangrijke informatie" sectie onderaan de offerte.
            </p>
          ) : (
            <p className="text-xs text-gray-500 italic">
              Wordt op de publieke pagina pas getoond zodra de pagina-template
              de intro-sectie expliciet rendert.
            </p>
          )}
        </>
      )}

      {sectionKey === "cta_band" && (
        <>
          <TextRow label="Titel" v={values.title} onChange={(v) => onChange("title", v)} disabled={!canEdit} />
          <AreaRow label="Beschrijving" v={values.description} onChange={(v) => onChange("description", v)} disabled={!canEdit} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextRow label="Primary CTA label" v={values.cta_primary_label} onChange={(v) => onChange("cta_primary_label", v)} disabled={!canEdit} />
            <TextRow label="Primary CTA URL" v={values.cta_primary_url} onChange={(v) => onChange("cta_primary_url", v)} disabled={!canEdit} />
            <TextRow label="Secondary CTA label" v={values.cta_secondary_label} onChange={(v) => onChange("cta_secondary_label", v)} disabled={!canEdit} />
            <TextRow label="Secondary CTA URL" v={values.cta_secondary_url} onChange={(v) => onChange("cta_secondary_url", v)} disabled={!canEdit} />
          </div>
          {slug === "offerte-share" ? (
            <p className="text-xs text-blue-900 italic">
              CTA-band is op de offerte share page bewust <strong>niet</strong>{" "}
              publiek zichtbaar; Bel/Mail-knoppen blijven hardcoded om de
              security van de share-link te bewaken.
            </p>
          ) : slug === "webshop" ? (
            <p className="text-xs text-gray-500 italic">
              Wordt op /webshop onderaan getoond door het bestaande CtaBand
              component. Laat de titel leeg om de band te verbergen.
            </p>
          ) : (
            <p className="text-xs text-gray-500 italic">
              Laat de titel leeg om de hele CTA-band te verbergen op deze pagina.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function TextRow({ label, v, onChange, testid, disabled }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input
        value={v ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        data-testid={testid ? `${testid}-input` : undefined}
      />
    </div>
  );
}

function AreaRow({ label, v, onChange, testid, disabled }) {
  return (
    <div>
      <Label>{label}</Label>
      <Textarea
        rows={3}
        value={v ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        data-testid={testid ? `${testid}-input` : undefined}
      />
    </div>
  );
}

function ListEditor({ label, items, fields, onChange, onAdd, onRemove, canEdit, max }) {
  return (
    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/50">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-[#082c50]">{label}</span>
        {canEdit && items.length < (max || 12) && (
          <Button size="sm" variant="outline" onClick={onAdd} className="rounded-lg text-xs">
            <Plus className="w-3 h-3 mr-1" /> Toevoegen
          </Button>
        )}
      </div>
      <div className="space-y-2">
        {items.map((it, idx) => (
          <div key={idx} className="flex flex-wrap items-end gap-2 p-2 bg-white rounded-lg border border-slate-200">
            {fields.map((f) => (
              <div key={f.key} className="flex-1 min-w-[140px]">
                <Label className="text-[10px] uppercase tracking-wide">{f.label}</Label>
                {f.type === "select" ? (
                  <select
                    value={it[f.key] || ""}
                    onChange={(e) => onChange(idx, f.key, e.target.value)}
                    disabled={!canEdit}
                    className="w-full h-9 px-2 rounded-lg border border-slate-200 text-sm"
                  >
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <Input
                    value={it[f.key] || ""}
                    onChange={(e) => onChange(idx, f.key, e.target.value)}
                    disabled={!canEdit}
                  />
                )}
              </div>
            ))}
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => onRemove(idx)} className="rounded-lg text-red-600">
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-xs text-gray-500 italic py-2">Nog geen items.</div>
        )}
      </div>
    </div>
  );
}
